import { HTTPService, JsonResultClass } from "./httpManager.js";
import { NotificationHelper } from "./notificationHelper.js";
import { DialogHelper } from "./dialogHelper.js";
import { ResourceSlot } from "../../resourcePlanningScheduler/capacityDiagram.js";
import { Helper } from "./helper.js";
import { ResourcePlanningSchedulerEventViewModel } from "../../resourcePlanningScheduler/resourcePlanningScheduler.js";
import { General } from "../../../siteManagement/general.js";
import { SheetEventType } from "../../../siteManagement/sheet.js";
import { SkillCategory } from "../../generalSettings/skills/skillsInterfaces.js";

export class ResourcePlanningHelper {
    public static reCalcualteSkills = (projnr: string, pindex: number, newStart, newEnd, resizeAction, byDelta, settings) => {
        return new Promise<boolean>(async (resolve) => {
            let result = await HTTPService.post<JsonResultClass>("Skills/ReCalculateAssignmentSkills",
                {
                    projNr: projnr, pIndex: pindex,
                    newStart: Helper.dates.getISODateString(newStart), newEnd: Helper.dates.getISODateString(newEnd),
                    resizeAction: resizeAction, byDelta: byDelta, settings: settings
                })
            if (result.Code == 1) {
                NotificationHelper.showErrorMessage(result.Message);
            }
            resolve(result.Code == 0);
        })
    }

    public static checkSkills = (projnr: string, pindex: number, targetRessnr: string, message: string): Promise<boolean> => {
        return new Promise<boolean>((resolve, reject) => {
            HTTPService.get<JsonResultClass>("Skills/CheckSkills?projNr=" + encodeURIComponent(projnr) + "&pIndex=" + pindex + "&targetRessNr=" + encodeURIComponent(targetRessnr))
                .then(async (result: JsonResultClass) => {
                    if (result.Code === 0) {
                        resolve(true);
                    }
                    else if (result.Code == 1) {
                        NotificationHelper.showErrorMessage(result.Message);
                        resolve(false);
                    }
                    else if (result.Code == 2) {
                        DialogHelper.confirm(message + result.Message).then(isOk => {
                            if (isOk) {
                                resolve(true);
                            } else {
                                resolve(false);
                            }
                        });
                    }
                });
        });
    }

    public static checkSkillsAssignment = (ressNr: string, skills: SkillCategory[]): Promise<boolean> => {
        return new Promise<boolean>((resolve, reject) => {
            HTTPService.post<JsonResultClass>("Skills/CheckSkillsAssignment?ressNr=" + ressNr, skills)
                .then(async (result: JsonResultClass) => {
                    if (result.Code === 0) {
                        resolve(true);
                    }
                    else if (result.Code == 1) {
                        NotificationHelper.showErrorMessage(result.Message);
                        resolve(false);
                    }
                    else if (result.Code == 2) {
                        const isOk = await DialogHelper.ask({ text: result.Message, header: Helper.getTranslation("WebSMS.ResourcePlanning.CheckSkillsAssignmentNegative"), isHtml: true, canCancel: true })
                        resolve(isOk)

                    }
                });
        });
    }

    public static checkSla = (workStart: Date, projNr: string, pIndex?: number, problemNr?: number, adrNr?: string, isUserTimezone?: boolean): Promise<boolean> => {
        return new Promise<boolean>((resolve, reject) => {
            HTTPService.get<JsonResultClass>("SlaTicketLevel/CheckSlaForAssignment", {
                projNr: projNr,
                pIndex: pIndex,
                workStart: Helper.dates.getISODateString(workStart),
                problemNr: problemNr,
                adrNr: adrNr,
                isUserTimezone: isUserTimezone
            }).then(async (result: JsonResultClass) => {
                if (result.Code === 0) {
                    resolve(true);
                }
                else if (result.Code === 1) {
                    NotificationHelper.showErrorMessage(result.Message);
                    resolve(false);
                }
                else if (result.Code === 2) {
                    DialogHelper.confirm(result.Message).then(isOk => {
                        if (isOk) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    });
                }
            }, (error) => {
                console.error(error);
                resolve(false);
            });
        });
    }

    public static getNewWorkStartForSlaCheck = (start: Date, workStart: Date, newStart: Date, useSchedulerHours: boolean) => {
        let newWorkStartMilliseconds = workStart.getTime() + (newStart.getTime() - start.getTime());
        let newWorkStart = new Date(newWorkStartMilliseconds);
        let newHours = useSchedulerHours ? newWorkStart.getHours() : workStart.getHours();
        let adjustedWorkStartDate = new Date(newWorkStart.getFullYear(), newWorkStart.getMonth(), newWorkStart.getDate(), newHours, workStart.getMinutes(), workStart.getSeconds());

        return adjustedWorkStartDate;
    }

    public static getSlotColor = (settings: Array<GeoMapRouteResultGraduation>, slot: ResourceSlot) => {
        let color: any = SchedulerGeoMapMatchColors.NoResult;
        if (slot.wayThere != null) {
            color = SchedulerGeoMapMatchColors.PoorMatch;
            if (slot.wayThere.Kind != 0) {
                if (settings != null && settings.length > 0) {
                    const sorted = settings.sort((s1, s2) => {
                        return s1.threshold - s2.threshold
                    });
                    sorted.some((setting) => {
                        if ((slot.wayThere.DistanceKm) <= setting.threshold) {
                            color = setting.color;
                            return true;
                        }
                    });
                } else {
                    if (slot.wayThere.DistanceKm < (100)) {
                        color = SchedulerGeoMapMatchColors.GoodMatch;
                    } else if (slot.wayThere.DistanceKm < (300)) {
                        color = SchedulerGeoMapMatchColors.MediumMatch;
                    } else {
                        color = SchedulerGeoMapMatchColors.PoorMatch;
                    }
                }
            }
        }
        return color;
    }

    public static getSlaColors = (eventView: ResourcePlanningSchedulerEventViewModel) => {
        let newBackgoundColor: string;
        if (eventView.SlaLevelDate != null) {
            if (kendo.parseDate(eventView.WorkStart) > kendo.parseDate(eventView.SlaLevelDate)) {
                newBackgoundColor = SlaMatchColors.BadDate;
            }
            else {
                newBackgoundColor = SlaMatchColors.GoodDate;
            }
        }
        else {
            newBackgoundColor = SlaMatchColors.NoSla;
        }
        return { color: "black", backgroundColor: newBackgoundColor } as SlaColors;
    }

    public static makeDraggableForScheduler = (e: HTMLElement, dataItem: ResourcePlanningSchedulerEventViewModel) => {
        let start = Helper.dates.toDate(dataItem.Start);
        let end = Helper.dates.toDate(dataItem.End);
        let duration = end.getTime() - start.getTime();

        let draggableItem: DayPilot.SchedulerMakeDraggableOptions = {
            element: e,
            data: {
                id: dataItem.Projnr + "|" + dataItem.Pindex,
                text: "Move",
                duration: new DayPilot.Duration(duration)
            }
        };
        DayPilot.Scheduler.makeDraggable(draggableItem);
    }

    public static makeDraggableForSlots = (e: HTMLElement, dataItem: ResourcePlanningSchedulerEventViewModel, canDrag: () => boolean) => {
        this.makeDraggable(e, dataItem, canDrag, "innosoft/drop-on-slot");
    }

    public static makeDraggableForDay = (e: HTMLElement, dataItem: ResourcePlanningSchedulerEventViewModel, canDrag: () => boolean) => {
        this.makeDraggable(e, dataItem, canDrag, "innosoft/drop-on-day");
    }

    public static makeDraggable = (e: HTMLElement, dataItem: ResourcePlanningSchedulerEventViewModel, canDrag: () => boolean, dragDropType: "innosoft/drop-on-slot" | "innosoft/drop-on-day") => {
        let ghostElem = ResourcePlanningHelper.getGhostElement();
        e.draggable = true;
        e.ondragstart = (ev) => {
            const data = {
                id: dataItem.Projnr + "|" + dataItem.Pindex
            };
            if (!canDrag())
                return false;
            ev.dataTransfer.setData(dragDropType, JSON.stringify(data));
            document.body.appendChild(ghostElem);
            ev.dataTransfer.setDragImage(ghostElem, 0, 0);
            ev.dataTransfer.effectAllowed = "move";
        };
        e.ondragend = (ev) => {
            document.body.removeChild(ghostElem);
        };
    }

    public static getGhostElement = () => {
        let elem = document.createElement("div");
        elem.id = "drag-ghost";
        elem.style.position = "absolute";
        elem.style.width = "100px";
        elem.style.height = "20px";
        elem.style.top = "-1000px";
        elem.style.opacity = "0.5";
        elem.style.backgroundColor = "rgb(170, 170, 170)";
        elem.style.overflow = "hidden";
        elem.style.pointerEvents = "none";
        return elem;
    }

    public static toggleFixedStart = async (event: ResourcePlanningSchedulerEventViewModel) => {
        const Fix = 6;
        const DeFix = 7;

        const result = await HTTPService.post<JsonResultClass<any>>('ResourcePlanningScheduler/ExecuteMultiSelectAction', {
            msAction: event.FixStart == 1 ? DeFix : Fix,
            ids: [event.IsGuid],
            additionalSettings: {
                fixMode: 1
            }
        });
        return result;
    }

    public static toggleFixedEnd = async (event: ResourcePlanningSchedulerEventViewModel) => {
        const Fix = 6;
        const DeFix = 7;

        const result = await HTTPService.post<JsonResultClass<any>>('ResourcePlanningScheduler/ExecuteMultiSelectAction', {
            msAction: event.FixEnd == 1 ? DeFix : Fix,
            ids: [event.IsGuid],
            additionalSettings: {
                fixMode: 2
            }
        });
        return result;
    }

    public static toggleFixedResource = async (event: ResourcePlanningSchedulerEventViewModel) => {
        const Fix = 6;
        const DeFix = 7;

        const result = await HTTPService.post<JsonResultClass<any>>('ResourcePlanningScheduler/ExecuteMultiSelectAction', {
            msAction: event.FixResource == 1 ? DeFix : Fix,
            ids: [event.IsGuid],
            additionalSettings: {
                fixMode: 4
            }
        });
        return result;
    }

    public static openResourceSearch = (params: {} = {}) => {
        if (!Helper.hasValue(params)) {
            params = {}
        }

        return new Promise<{}>((resolve) => {
            let url = "DynamicSearch/Window/Resource?fullObject=true&cus_disableRECHTPE=true&cus_disablePERSONALDATENRECHT=true&resstatus=0%20or%20null&disabled=resstatus"
            Object.keys(params).forEach((key) => {
                url += `&${key}=${encodeURI(params[key])}`
            })
            const sheet = General.Sheet.Manager.createSheetWithUrl(url);
            sheet.bind(SheetEventType.RowClicked, (dataItem) => {
                resolve(dataItem)
            })
        })
    }
}

enum SlaMatchColors {
    GoodDate = "#32E007",
    TightDate = "yellow",
    BadDate = "red",
    NoSla = "white"
}

export interface GeoMapRouteResultGraduation {
    threshold?: number;
    color?: string;
    id: string;
}

export enum SchedulerGeoMapMatchColors {
    GoodMatch = "#a5d6a7",
    MediumMatch = "#ffe082",
    PoorMatch = "#ef9a9a",
    NoResult = "#e0e0e0"
}

export interface SlaColors {
    color: string,
    backgroundColor: string;
}
